/**
 * How a row the return does not carry — or cannot yet file — reads to a person.
 *
 * One definition for the gates panel on Returns and the Reports issue list, so the two cannot
 * drift: they are "the same computation" (the Returns subtitle says so) and must read alike.
 *
 * Found 2026-09-29: both printed `hsn_missing on row 5335b40c374d — blocks prepared` — a check
 * code and the first twelve characters of a content hash — and on one real month there were
 * 5,891 such lines, unpaged, not one of them findable from what was shown. The service now sends
 * each row's invoice, date, party, HSN and source file, and `why` in words (`returns/reasons.py`).
 * A service older than that sends neither; this falls back to saying so rather than to the hash.
 */

export interface GateRow {
  content_key?: unknown
  invoice_no?: unknown
  invoice_date?: unknown
  party?: unknown
  item?: unknown
  hsn?: unknown
  source?: unknown
  why?: unknown
  /** The check names the row is held on (`pos_code`, …), as the draft lists them. */
  failing?: unknown
}

const text = (value: unknown): string | null =>
  value === null || value === undefined || String(value).trim() === '' ? null : String(value).trim()

/**
 * "Invoice INV-1 · 12/07/2026 · 27AXSPB4752H1Z8 · Widget · HSN 4071990 · register.xlsx", leaving
 * out what the row does not carry. The item and the HSN say WHICH line of a many-line invoice —
 * one real month listed the same invoice three times.
 */
export function rowWhere(row: GateRow): string {
  const invoice = text(row.invoice_no)
  const hsn = text(row.hsn)
  const parts = [
    invoice ? `Invoice ${invoice}` : 'A row with no invoice number',
    text(row.invoice_date),
    text(row.party),
    text(row.item),
    hsn ? `HSN ${hsn}` : null,
    text(row.source),
  ].filter((part): part is string => part !== null)
  return parts.join(' · ')
}

/** Why it is out, in the service's words; never a code. */
export function rowWhy(row: GateRow): string {
  return text(row.why) ?? 'Not given — this service is older than the one that says why'
}

/**
 * What the preparer does about it, where this screen knows (D-144). Said here and not in `why`,
 * because `why` is also printed in the client's workbook, and a client has no Raw Data.
 */
export function rowNext(row: GateRow): string | null {
  const failing = Array.isArray(row.failing) ? row.failing : []
  if (failing.includes('pos_code') || failing.includes('pos_unresolved')) {
    return 'Set its place of supply with Correct… on Raw Data.'
  }
  // D-148: the stated invoice type does not fit the row; Raw Data opens Correct… on it.
  if (failing.some((code) => code.startsWith('invoice_type_'))) {
    return 'Correct its invoice type, or what the invoice type contradicts, with Correct… on Raw Data.'
  }
  // D-151: a foreign-currency sale with no rupees yet — a person types the rate on Raw Data.
  if (failing.includes('needs_inr_value')) {
    return 'Type the rate with Convert at a rate… on Raw Data, or bind the value-in-currency column if its amounts are already rupees.'
  }
  return null
}

/** Rows grouped by their reason, most first — the summary a long list opens with. */
export function reasonCounts(rows: GateRow[]): Array<{ why: string; count: number }> {
  const counts = new Map<string, number>()
  for (const row of rows) {
    const why = rowWhy(row)
    counts.set(why, (counts.get(why) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([why, count]) => ({ why, count }))
    .sort((a, b) => b.count - a.count || a.why.localeCompare(b.why))
}

/** How many rows a list shows before "Show more" — 5,891 lines is not a list anyone reads. */
export const GATE_PAGE = 25
